import { devWarn } from "@/lib/log";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  House, Package, Users, CreditCard, Wallet, UsersThree, UserCircle, SignOut, Plus,
  DownloadSimple, UserCircleGear, FileText, Truck, Scales, Tag, Globe,
} from "@phosphor-icons/react";
import NotificationsBell from "@/components/NotificationsBell";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: House, roles: ["admin", "finance", "shipping", "sales"] },
  { to: "/orders", label: "Orders", icon: Package, roles: ["admin", "finance", "shipping", "sales"] },
  { to: "/customers", label: "Customers", icon: Users, roles: ["admin", "finance", "sales"] },
  { to: "/products", label: "Products", icon: Tag, roles: ["admin", "finance", "sales"] },
  { to: "/pricing-zones", label: "Pricing Zones", icon: Globe, roles: ["admin", "finance"] },
  { to: "/manifests", label: "Manifests", icon: Truck, roles: ["admin", "shipping", "finance"] },
  { to: "/invoices", label: "Invoices", icon: FileText, roles: ["admin", "finance"] },
  { to: "/reconciliation", label: "Reconciliation", icon: Scales, roles: ["admin", "finance"] },
  { to: "/payment-accounts", label: "Payment Accounts", icon: CreditCard, roles: ["admin", "finance"] },
  { to: "/receivers", label: "Receivers", icon: Wallet, roles: ["admin", "finance"] },
  { to: "/imports", label: "Imports", icon: DownloadSimple, roles: ["admin", "finance"] },
  { to: "/users", label: "Users", icon: UsersThree, roles: ["admin"] },
  { to: "/portal", label: "My Portal", icon: UserCircleGear, roles: ["receiver"] },
];

export default function DashboardLayout({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { devWarn("Logout call failed — clearing local session anyway", e); }
    setUser(null);
    toast.success("Signed out");
    navigate("/login");
  };

  const items = NAV.filter((n) => n.roles.includes(user.role));
  const isReceiver = user.role === "receiver";

  return (
    <div className="min-h-screen grid grid-cols-[240px_1fr] bg-white">
      {/* Sidebar */}
      <aside className="border-r border-zinc-200 bg-zinc-50 flex flex-col" data-testid="sidebar">
        <div className="p-5 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <Package size={22} weight="bold" />
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase font-semibold">Courier ERP</span>
          </div>
        </div>

        <nav className="flex-1 py-4 px-2">
          <p className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-semibold">Menu</p>
          {items.map((it) => {
            const active = location.pathname.startsWith(it.to);
            const Icon = it.icon;
            return (
              <Link key={it.to} to={it.to}
                className={`flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-md text-sm transition-colors ${
                  active ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-200"
                }`}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon size={18} weight={active ? "fill" : "regular"} />
                <span>{it.label}</span>
              </Link>
            );
          })}

          {!isReceiver && (
            <div className="mt-6">
              <button onClick={() => navigate("/orders/new")}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-sm border border-zinc-300 hover:bg-zinc-100 transition-colors font-medium"
                data-testid="new-order-quick-button">
                <Plus size={16} weight="bold" /> New Order
              </button>
            </div>
          )}
        </nav>

        <div className="p-3 border-t border-zinc-200">
          <div className="flex items-center gap-3 px-2 py-2">
            <UserCircle size={32} weight="light" className="text-zinc-500" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate" data-testid="user-name">{user.name || user.email}</p>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500" data-testid="user-role">{user.role}</p>
            </div>
          </div>
          <button onClick={handleLogout}
            className="w-full mt-2 flex items-center gap-2 px-3 py-2 rounded-md text-sm text-zinc-600 hover:bg-zinc-200 transition-colors"
            data-testid="logout-button">
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="min-w-0 flex flex-col">
        {/* Top bar with notifications */}
        <header className="h-14 border-b border-zinc-200 bg-white flex items-center justify-end px-6 gap-2" data-testid="topbar">
          <NotificationsBell />
        </header>
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
